import { Component, Input, OnChanges } from "@angular/core";

export type HomeMiniKind = "return" | "risk" | "drawdown" | "concentration";

export interface HomeMiniWeight {
  label: string;
  weight: number;
}

@Component({
  selector: "app-exp-home-mini",
  standalone: true,
  imports: [],
  template: `
    @if (isPlaceholder()) {
      <div
        class="mini-chart-skeleton"
        [style.width.px]="W"
        [style.height.px]="H"
        [attr.aria-label]="'Loading ' + kind + ' chart'"
      >
        @switch (kind) {
          @case ("concentration") {
            <div class="mini-chart-skeleton__rows">
              @for (row of skeletonRows; track row) {
                <div class="mini-chart-skeleton__row">
                  <span class="shimmer mini-chart-skeleton__lbl"></span>
                  <span
                    class="shimmer mini-chart-skeleton__bar"
                    [style.width.%]="row"
                  ></span>
                </div>
              }
            </div>
          }
          @default {
            <div
              class="shimmer mini-chart-skeleton__shape"
              [attr.data-kind]="kind"
            ></div>
          }
        }
      </div>
    }

    @if (!isPlaceholder()) {
      @switch (kind) {
        <!-- Return line -->
        @case ("return") {
          <svg
            [attr.viewBox]="'0 0 ' + W + ' ' + H"
            [attr.width]="W"
            [attr.height]="H"
            style="display:block; overflow:visible;"
          >
            <line
              [attr.x1]="padL"
              [attr.y1]="startY"
              [attr.x2]="padL + innerW"
              [attr.y2]="startY"
              stroke="var(--border-strong)"
              stroke-dasharray="2 3"
              stroke-opacity="0.6"
            />
            <path [attr.d]="areaD" [attr.fill]="color" fill-opacity="0.14" />
            <path
              [attr.d]="pathD"
              fill="none"
              [attr.stroke]="color"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              [attr.cx]="endX"
              [attr.cy]="endY"
              r="3"
              [attr.fill]="color"
            />
            <text
              [attr.x]="endX + 5"
              [attr.y]="endY + 4"
              [style.--svg-text]="color"
              style="--svg-text-weight:600"
              font-size="10.5"
              font-family="var(--font-mono)"
            >
              {{ endText }}
            </text>
            <text
              [attr.x]="padL + 2"
              [attr.y]="startY - 3"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              font-family="var(--font-mono)"
            >
              {{ startText }}
            </text>
            <text
              [attr.x]="padL"
              [attr.y]="H - 4"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              letter-spacing="0.04em"
            >
              START
            </text>
            <text
              [attr.x]="padL + innerW"
              [attr.y]="H - 4"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              letter-spacing="0.04em"
              text-anchor="end"
            >
              NOW
            </text>
          </svg>
        }
        <!-- Risk / volatility bars -->
        @case ("risk") {
          <svg
            [attr.viewBox]="'0 0 ' + W + ' ' + H"
            [attr.width]="W"
            [attr.height]="H"
            style="display:block; overflow:visible;"
          >
            <line
              [attr.x1]="padLRisk"
              [attr.y1]="padT"
              [attr.x2]="padLRisk + innerWRisk"
              [attr.y2]="padT"
              stroke="var(--border)"
              stroke-opacity="0.5"
              stroke-dasharray="2 3"
            />
            <line
              [attr.x1]="padLRisk"
              [attr.y1]="padT + innerHRisk"
              [attr.x2]="padLRisk + innerWRisk"
              [attr.y2]="padT + innerHRisk"
              stroke="var(--border-strong)"
              stroke-opacity="0.6"
            />
            <text
              [attr.x]="padLRisk - 4"
              [attr.y]="padT + 4"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              font-family="var(--font-mono)"
              text-anchor="end"
            >
              {{ axisMax }}%
            </text>
            <text
              [attr.x]="padLRisk - 4"
              [attr.y]="padT + innerHRisk + 3"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              font-family="var(--font-mono)"
              text-anchor="end"
            >
              0
            </text>
            <line
              [attr.x1]="padLRisk"
              [attr.y1]="refY"
              [attr.x2]="padLRisk + innerWRisk"
              [attr.y2]="refY"
              [attr.stroke]="color"
              stroke-dasharray="3 2"
              stroke-opacity="0.45"
            />
            @for (b of riskBars; track b) {
              <rect
                [attr.x]="b.x"
                [attr.y]="b.y"
                [attr.width]="b.w"
                [attr.height]="b.h"
                rx="1.5"
                [attr.fill]="color"
                [attr.fill-opacity]="b.last ? 1 : 0.55"
              />
            }
            <text
              [attr.x]="riskLastX + 4"
              [attr.y]="riskLastY + 4"
              [style.--svg-text]="color"
              style="--svg-text-weight:600"
              font-size="10.5"
              font-family="var(--font-mono)"
            >
              {{ riskLastText }}
            </text>
            @for (lbl of riskLabels; track lbl) {
              <text
                [attr.x]="lbl.x"
                [attr.y]="H - 4"
                style="--svg-text: var(--text-faint);"
                font-size="9.5"
                text-anchor="middle"
              >
                {{ lbl.text }}
              </text>
            }
          </svg>
        }
        <!-- Drawdown curve (underwater) -->
        @case ("drawdown") {
          <svg
            [attr.viewBox]="'0 0 ' + W + ' ' + H"
            [attr.width]="W"
            [attr.height]="H"
            style="display:block; overflow:visible;"
          >
            <line
              [attr.x1]="padLDd"
              [attr.y1]="padT"
              [attr.x2]="padLDd + innerWDd"
              [attr.y2]="padT"
              stroke="var(--border-strong)"
              stroke-dasharray="2 3"
              stroke-opacity="0.7"
            />
            <text
              [attr.x]="padLDd - 4"
              [attr.y]="padT + 4"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              font-family="var(--font-mono)"
              text-anchor="end"
            >
              0%
            </text>
            <line
              [attr.x1]="padLDd"
              [attr.y1]="padT + innerHDd"
              [attr.x2]="padLDd + innerWDd"
              [attr.y2]="padT + innerHDd"
              stroke="var(--border)"
              stroke-opacity="0.3"
              stroke-dasharray="2 3"
            />
            <text
              [attr.x]="padLDd - 4"
              [attr.y]="padT + innerHDd + 3"
              style="--svg-text: var(--text-faint);"
              font-size="9.5"
              font-family="var(--font-mono)"
              text-anchor="end"
            >
              {{ ddAxisMin }}%
            </text>
            <path [attr.d]="ddAreaD" [attr.fill]="color" fill-opacity="0.22" />
            <path
              [attr.d]="ddPathD"
              fill="none"
              [attr.stroke]="color"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              [attr.cx]="ddTroughX"
              [attr.cy]="ddTroughY"
              r="2.8"
              [attr.fill]="color"
            />
            <text
              [attr.x]="ddTroughX"
              [attr.y]="ddTroughY + 12"
              [style.--svg-text]="color"
              style="--svg-text-weight:600"
              font-size="10"
              font-family="var(--font-mono)"
              text-anchor="middle"
            >
              {{ ddTroughText }}
            </text>
            <circle
              [attr.cx]="ddEndX"
              [attr.cy]="ddEndY"
              r="2.5"
              [attr.fill]="color"
            />
            <text
              [attr.x]="ddEndX + 4"
              [attr.y]="ddEndY + 3.5"
              [style.--svg-text]="color"
              style="--svg-text-weight:600"
              font-size="10.5"
              font-family="var(--font-mono)"
            >
              {{ ddEndText }}
            </text>
          </svg>
        }
        <!-- Concentration: top holdings bars -->
        @case ("concentration") {
          <div class="mini-holdings">
            @for (r of concRows; track r) {
              <div class="mh-row">
                <div class="mh-lbl">{{ r.label }}</div>
                <div class="mh-track">
                  <div
                    class="mh-fill"
                    [style.width.%]="r.pct"
                    [style.background]="r.color"
                  ></div>
                </div>
                <div class="mh-val">{{ r.val }}%</div>
              </div>
            }
          </div>
        }
      }
    }
  `,
})
export class HomeMiniChartComponent implements OnChanges {
  @Input() kind: HomeMiniKind = "return";
  @Input() series: number[] = [];
  @Input() weights: HomeMiniWeight[] = [];
  @Input() color = "#7C5CFF";
  @Input() loading = false;

  readonly W = 300;
  readonly H = 72;
  readonly padT = 8;

  padL = 6;
  padR = 38;
  padB = 18;
  innerW = 0;
  innerH = 0;
  startY = 0;
  endX = 0;
  endY = 0;
  pathD = "";
  areaD = "";
  startText = "";
  endText = "";

  padLRisk = 26;
  padRRisk = 30;
  innerWRisk = 0;
  innerHRisk = 0;
  axisMax = 0;
  refY = 0;
  riskBars: { x: number; y: number; w: number; h: number; last: boolean }[] =
    [];
  riskLastX = 0;
  riskLastY = 0;
  riskLastText = "";
  riskLabels: { x: number; text: string }[] = [];

  padLDd = 30;
  padRDd = 36;
  innerWDd = 0;
  innerHDd = 0;
  ddAxisMin = 0;
  ddPathD = "";
  ddAreaD = "";
  ddTroughX = 0;
  ddTroughY = 0;
  ddTroughText = "";
  ddEndX = 0;
  ddEndY = 0;
  ddEndText = "";

  concRows: { label: string; pct: number; val: string; color: string }[] = [];

  readonly skeletonRows = [78, 58, 42, 26];

  isPlaceholder(): boolean {
    if (!this.loading) return false;
    if (this.kind === "concentration") {
      return !this.weights || this.weights.length === 0;
    }
    const valid = (this.series || []).filter(
      (v) => typeof v === "number" && isFinite(v),
    );
    return valid.length < 2;
  }

  ngOnChanges(): void {
    if (this.kind === "return") this.computeReturn();
    else if (this.kind === "risk") this.computeRisk();
    else if (this.kind === "drawdown") this.computeDrawdown();
    else if (this.kind === "concentration") this.computeConcentration();
  }

  private computeReturn(): void {
    const s = this.series.filter((v) => typeof v === "number" && isFinite(v));
    if (s.length < 2) {
      this.pathD = "";
      this.areaD = "";
      return;
    }
    const padH = this.padB - 8;
    this.innerW = this.W - this.padL - this.padR;
    this.innerH = this.H - this.padT - padH - 10;
    const min = Math.min(...s);
    const max = Math.max(...s);
    const range = max - min || 1;
    const sx = (i: number) => this.padL + (i / (s.length - 1)) * this.innerW;
    const sy = (v: number) =>
      this.padT + this.innerH - ((v - min) / range) * this.innerH;
    const start = s[0];
    const end = s[s.length - 1];
    this.startY = sy(start);
    this.pathD = s
      .map(
        (v, i) =>
          `${i === 0 ? "M" : "L"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`,
      )
      .join(" ");
    this.areaD = `${this.pathD} L${sx(s.length - 1).toFixed(1)},${sy(min).toFixed(1)} L${sx(0).toFixed(1)},${sy(min).toFixed(1)} Z`;
    this.endX = sx(s.length - 1);
    this.endY = sy(end);
    this.endText = end.toFixed(0);
    this.startText = start.toFixed(0);
  }

  private computeRisk(): void {
    const values = this.series.filter(
      (v) => typeof v === "number" && isFinite(v) && v >= 0,
    );
    if (!values.length) {
      this.riskBars = [];
      return;
    }
    this.innerWRisk = this.W - this.padLRisk - this.padRRisk;
    this.innerHRisk = this.H - this.padT - 16;
    const maxV = Math.max(...values);
    this.axisMax = Math.ceil(maxV / 10) * 10 || 10;
    const gap = 3;
    const bw = (this.innerWRisk - gap * (values.length - 1)) / values.length;
    const refV = values[0];
    this.refY =
      this.padT + this.innerHRisk - (refV / this.axisMax) * this.innerHRisk;
    this.riskBars = values.map((v, i) => {
      const h = (v / this.axisMax) * this.innerHRisk;
      const x = this.padLRisk + i * (bw + gap);
      const y = this.padT + this.innerHRisk - h;
      return { x, y, w: bw, h: Math.max(1, h), last: i === values.length - 1 };
    });
    const lastIdx = values.length - 1;
    this.riskLastX = this.padLRisk + lastIdx * (bw + gap) + bw;
    this.riskLastY =
      this.padT +
      this.innerHRisk -
      (values[lastIdx] / this.axisMax) * this.innerHRisk;
    this.riskLastText = values[lastIdx].toFixed(1) + "%";
    const MONTH_LBL = [
      "J",
      "F",
      "M",
      "A",
      "M",
      "J",
      "J",
      "A",
      "S",
      "O",
      "N",
      "D",
    ];
    const now = new Date();
    this.riskLabels = [0, Math.floor(values.length / 2), values.length - 1].map(
      (i) => {
        const d = new Date(
          now.getFullYear(),
          now.getMonth() - (values.length - 1 - i),
          1,
        );
        const x = this.padLRisk + i * (bw + gap) + bw / 2;
        return { x, text: MONTH_LBL[d.getMonth()] };
      },
    );
  }

  private computeDrawdown(): void {
    const values = this.series.filter(
      (v) => typeof v === "number" && isFinite(v),
    );
    if (values.length < 2) {
      this.ddPathD = "";
      this.ddAreaD = "";
      return;
    }
    this.innerWDd = this.W - this.padLDd - this.padRDd;
    this.innerHDd = this.H - this.padT - 16;
    const min = Math.min(...values, 0);
    this.ddAxisMin = Math.floor(min / 5) * 5 || -5;
    const sx = (i: number) =>
      this.padLDd + (i / (values.length - 1)) * this.innerWDd;
    const sy = (v: number) => this.padT + (v / this.ddAxisMin) * this.innerHDd;
    const pts = values.map((v, i) => [sx(i), sy(v)] as const);
    this.ddPathD = pts
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`,
      )
      .join(" ");
    this.ddAreaD = `${this.ddPathD} L${sx(values.length - 1).toFixed(1)},${this.padT.toFixed(1)} L${sx(0).toFixed(1)},${this.padT.toFixed(1)} Z`;
    let troughIdx = 0;
    values.forEach((v, i) => {
      if (v < values[troughIdx]) troughIdx = i;
    });
    this.ddTroughX = sx(troughIdx);
    this.ddTroughY = sy(values[troughIdx]);
    this.ddTroughText = values[troughIdx].toFixed(1) + "%";
    this.ddEndX = sx(values.length - 1);
    this.ddEndY = sy(values[values.length - 1]);
    this.ddEndText = values[values.length - 1].toFixed(1) + "%";
  }

  private computeConcentration(): void {
    const palette = ["#FFB547", "#A78BFF", "#7C5CFF", "#54C7FF"];
    const rows = this.weights.slice(0, 4);
    if (!rows.length) {
      this.concRows = [];
      return;
    }
    const max = Math.max(...rows.map((r) => r.weight), 0.0001);
    this.concRows = rows.map((r, i) => {
      const isOther = r.label === "Other";
      return {
        label: r.label,
        val: r.weight.toFixed(1),
        pct: (r.weight / max) * 100,
        color: isOther ? "var(--surface-3)" : palette[i] || "var(--accent)",
      };
    });
  }
}
